import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { dishInitials } from "@/components/DishRow";
import { PHOTO } from "@/constants/testIds";
import { useSheetHistory } from "@/hooks/useSheetHistory";

/**
 * Tap-to-enlarge. A dish with a photo shows it full-bleed in the same rounded
 * square; a dish without one keeps the initials tile, which is the common case and
 * should read as a deliberate design, not a failed image. A photo that 404s (the
 * file was removed behind the dish's back) falls back to that same tile.
 */
const PhotoOverlay = ({ dish, onClose }) => {
  useSheetHistory(true, onClose);
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  // A different dish deserves a fresh attempt at its own photo.
  useEffect(() => setFailed(false), [dish?.id, dish?.image_url]);

  if (!dish) return null;
  const src = failed ? null : mediaUrl(dish.image_url);

  return (
    <div
      data-testid={PHOTO.overlay}
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <button
        data-testid={PHOTO.overlayClose}
        onClick={onClose}
        className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center backdrop-blur"
        aria-label={t("common.close")}
      >
        <X size={22} />
      </button>

      {src ? (
        <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <img
            data-testid={PHOTO.overlayImage}
            src={src}
            alt={dish.name}
            onError={() => setFailed(true)}
            className="w-full aspect-square object-cover rounded-3xl bg-neutral-900"
          />
          <div className="text-white text-lg font-bold mt-4 text-center">{dish.name}</div>
        </div>
      ) : (
        <div
          data-photo-placeholder="true"
          data-testid={PHOTO.overlayInitials}
          className="w-full max-w-md aspect-square bg-neutral-900 rounded-3xl flex items-center justify-center text-center px-8 relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-700 to-neutral-900" />
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "4px 4px" }}
          />
          <div className="relative flex flex-col items-center">
            <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
              {t("photo.placeholder")}
            </div>
            <div
              className="w-40 h-40 rounded-3xl bg-white/5 border border-white/10 flex items-center justify-center"
              aria-hidden="true"
            >
              <span className="text-white text-6xl font-black tracking-tight">
                {dishInitials(dish.name)}
              </span>
            </div>
            <div className="text-white text-lg font-bold mt-4 text-center max-w-xs">{dish.name}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoOverlay;
